import { Terrain } from './terrain';
import { attackTiming } from './attack-timing';
import { sourceShotGeometry } from './shot-geometry';
import { sourceFallDuration, sourceFallY, sourceStandingY, sourceWalk } from './walk-geometry';
import { roomAssetKey } from './room-assets-key';
import spawnConfiguration from './map-spawns.json';
import { sourceBombGroundEmpty, sourceBombSweep, sourceFlyTranslatePoint, sourcePlayerBlastDistance, sourcePlayerDamageDistance, sourcePlayerIntersectsBomb } from './source-collision';
import type { BallDefinition, ClientCommand, CraterOperation, DamageEvent, EngineResult, GameData, GameState, MapDefinition, MovementFall, PlayerState, PlayerTransition, Point, ProjectileResult, PropId, ShotResult, Team, WeaponDefinition } from './types';

export const TURN_MS = 30_000;
export const PHYSICS_STEP = 0.04;
/** Candidate values from Gunny 3.0 TemplateAlllist.xml (10001–10008/10015/10016).
 * These are explicit later-build baselines, not verified 2009 balance. */
export const PROP_DEFINITIONS: Record<PropId, { name: string; energy: number; delay: number }> = {
  damage10: { name: '伤害 +10%', energy: 50, delay: 70 },
  damage20: { name: '伤害 +20%', energy: 55, delay: 150 },
  damage30: { name: '伤害 +30%', energy: 70, delay: 200 },
  damage40: { name: '伤害 +40%', energy: 80, delay: 340 },
  damage50: { name: '伤害 +50%', energy: 85, delay: 400 },
  extra1: { name: '附加一次攻击', energy: 110, delay: 500 },
  extra2: { name: '附加两次攻击', energy: 110, delay: 600 },
  trident: { name: '三叉戟', energy: 110, delay: 600 },
  fly: { name: '纸飞机', energy: 150, delay: 200 },
  ice: { name: '冰冻弹', energy: 150, delay: 700 },
};

function assert(condition: unknown, message: string): asserts condition { if (!condition) throw new Error(message); }
function finite(value: unknown, min: number, max: number, label: string): number {
  assert(typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max, `${label}无效`); return value;
}
function clone(state: GameState): GameState { return structuredClone(state); }
function name(value: unknown): string {
  assert(typeof value === 'string', '昵称无效');
  const result = value.trim().replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 16);
  assert(result.length > 0, '请输入昵称'); return result;
}
function random(state: GameState): number {
  state.rng = (Math.imul(state.rng >>> 0, 1664525) + 1013904223) >>> 0;
  return state.rng / 0x100000000;
}
interface Flight { points: { x: number; y: number; t: number }[]; impact: Point | null; duration: number }
interface FlightMotion extends Point { vx: number; vy: number }
interface ActionDefinition {
  projectile: string[]; explosion: string[];
  candidatePhysics: Pick<BallDefinition, 'power' | 'radius' | 'mass' | 'weight' | 'wind' | 'drag'>;
}
interface MapSpawnConfiguration { width: number; height: number; teams: Point[][] }
const mapSpawns: Record<string, MapSpawnConfiguration> = spawnConfiguration.maps;

/** Authoritative, serializable, room-local battle state. No wall-clock simulation
 * runs between commands. Evidence: research/source-inspection/formula-audit/
 * findings.json. Browser animation does not affect collision or damage. */
export class BattleEngine {
  readonly maps: Map<string, MapDefinition>;
  readonly weapons: Map<string, WeaponDefinition>;
  private originals = new Map<string, Terrain>();
  private craterMasks = new Map<string, Terrain>();

  constructor(readonly gameData: GameData) {
    this.maps = new Map(Object.values(gameData.maps).map(m => [String(m.id), m]));
    this.weapons = new Map(Object.values(gameData.weapons).map(w => [String(w.id), w]));
    assert(this.maps.size && this.weapons.size, '地图或武器数据缺失');
  }

  loadTerrain(mapId: string | number, mask: ArrayBuffer | Uint8Array, craters: Record<string, ArrayBuffer | Uint8Array>): void {
    const map = this.map(String(mapId)), terrain = Terrain.fromBuffer(mask);
    assert(map.width === terrain.width && map.height === terrain.height, '地图与碰撞尺寸不符');
    this.originals.set(String(mapId), terrain);
    for (const [id, data] of Object.entries(craters)) this.craterMasks.set(String(id), Terrain.fromBuffer(data));
  }

  createState(): GameState {
    return { phase: 'lobby', mapId: this.maps.keys().next().value!, players: [],
      turn: { playerId: '', number: 0, deadline: 0, readyAt: 0, wind: 0 }, winner: null,
      revision: 0, round: 0, rng: 0x6d2b79f5, craters: [], lastShot: null };
  }

  getTerrain(state: GameState): Terrain {
    const original = this.originals.get(String(state.mapId));
    assert(original, '地图碰撞数据尚未加载');
    const terrain = original.clone(); terrain.replay(state.craters, this.craterMasks); return terrain;
  }

  private map(id: string): MapDefinition { const map = this.maps.get(id); assert(map, '地图不存在'); return map; }
  private weapon(id: string): WeaponDefinition { const w = this.weapons.get(id); assert(w, '武器不存在'); return w; }
  private actionBall(kind: 'fly' | 'ice'): BallDefinition {
    const actions = this.gameData.actions as Record<'fly' | 'ice', ActionDefinition> | undefined;
    const action = actions?.[kind], physics = action?.candidatePhysics;
    assert(action && physics && ['power', 'radius', 'mass', 'weight', 'wind', 'drag'].every(key => Number.isFinite(physics[key as keyof typeof physics]))
      && physics.mass > 0 && Array.isArray(action.projectile) && action.projectile.length && Array.isArray(action.explosion), '动作弹药配置尚未加载');
    return { ...physics, projectile: action.projectile, explosion: action.explosion,
      amount: 1, spin: 0, craterId: '', craterMask: '', crater: '' };
  }
  private player(state: GameState, id: string): PlayerState { const p = state.players.find(p => p.id === id); assert(p, '玩家不在房间中'); return p; }
  private human(state: GameState, id: string): PlayerState {
    const p = this.player(state, id);
    assert(!p.bot && p.connected, '只有房间内在线真人可以执行此操作');
    return p;
  }
  private lobby(state: GameState): void { assert(state.phase === 'lobby', '对局开始后不能修改房间'); }
  private requiresNewRoomAssets(before: GameState, after: GameState): boolean {
    const loadedWeapons = new Set(before.players.map(p => p.weaponId));
    return before.mapId !== after.mapId || after.players.some(p => !loadedWeapons.has(p.weaponId));
  }
  private turnPlayer(state: GameState, id: string, now: number): PlayerState {
    assert(state.phase === 'playing' && state.turn.playerId === id, '尚未轮到你');
    const p = this.player(state, id);
    assert(now >= state.turn.readyAt, p.movementFall && now < p.movementFall.startedAt + p.movementFall.duration ? '角色正在下落' : '上一发弹药仍在飞行');
    assert(now < state.turn.deadline, '本回合已超时');
    assert(p.alive && !p.frozenTurns && (p.bot || p.connected), '当前不能行动'); return p;
  }
  private energy(w: WeaponDefinition): number { return 240 + Math.floor(w.agility / 30); }
  private baseDelay(w: WeaponDefinition): number {
    // Later TurnedLiving.cs/Player.cs base delay, using integer divisions.
    return 1600 - Math.floor(1200 * w.agility / (w.agility + 1200)) + Math.floor(w.attack / 10);
  }
  private newPlayer(id: string, displayName: string, team: Team, weaponId: string, bot: boolean): PlayerState {
    const w = this.weapon(weaponId);
    return { id, name: displayName, team, weaponId, bot, connected: true, ready: bot, x: 0, y: 0, face: team === 0 ? 1 : -1,
      hp: 1500, maxHp: 1500, energy: this.energy(w), anger: 0, angle: Math.round((w.minAngle + w.maxAngle) / 2), alive: true, delay: 0, frozenTurns: 0, queuedProps: [], queuedPow: false };
  }

  apply(input: GameState, playerId: string, command: ClientCommand, now = Date.now()): EngineResult {
    assert(command && typeof command === 'object' && typeof command.type === 'string', '指令无效');
    assert(Number.isFinite(now), '时间无效');
    const state = clone(input);
    let shot: ShotResult | undefined;
    switch (command.type) {
      case 'join': {
        assert(typeof playerId === 'string' && playerId.length > 0 && playerId.length <= 128, '玩家身份无效');
        const displayName = name(command.name), existing = state.players.find(p => p.id === playerId);
        if (existing) { assert(!existing.bot, '机器人席位不可登录'); existing.name = displayName; existing.connected = true; break; }
        this.lobby(state); assert(state.players.length < 6, '房间已满');
        const counts = [state.players.filter(p => p.team === 0).length, state.players.filter(p => p.team === 1).length];
        const team: Team = counts[0] <= counts[1] ? 0 : 1;
        state.players.push(this.newPlayer(playerId, displayName, team, this.weapons.keys().next().value!, false));
        break;
      }
      case 'name': this.human(state, playerId).name = name(command.name); break;
      case 'leave': {
        // The authenticated room service also accepts deliberate exits over
        // HTTP after the socket has dropped. A reserved offline human must
        // be able to release their own seat; bot identities still cannot exit.
        const p = this.player(state, playerId); assert(!p.bot, '只有真人玩家可以主动退出');
        // Resolve victory while the departing side is still represented; after
        // removal a contested battle must not become one-team practice.
        this.kill(p);
        if (state.phase === 'playing') this.checkWinner(state);
        state.players = state.players.filter(player => player.id !== playerId);
        if (state.phase === 'playing' && state.turn.playerId === playerId) {
          this.nextTurn(state, Math.max(now, state.turn.readyAt));
        }
        break;
      }
      case 'team': {
        this.lobby(state); this.human(state, playerId); finite(command.team, 0, 1, '队伍'); assert(command.team === 0 || command.team === 1, '队伍无效');
        const target = command.playerId ?? playerId;
        const p = this.player(state, target);
        if (target !== playerId) assert(p.bot, '其他玩家自行选择队伍');
        assert(state.players.filter(x => x.id !== p.id && x.team === command.team).length < 3, '每队最多三人');
        p.team = command.team; p.ready = p.bot; p.face = p.team === 0 ? 1 : -1; break;
      }
      case 'weapon': {
        this.lobby(state); this.human(state, playerId); const target = command.playerId ?? playerId;
        if (target !== playerId) assert(this.player(state, target).bot, '其他玩家自行选择武器');
        const w = this.weapon(String(command.weaponId)), p = this.player(state, target);
        p.weaponId = String(w.id); p.angle = Math.round((w.minAngle + w.maxAngle) / 2); p.ready = p.bot; break;
      }
      case 'ready':
        this.lobby(state); assert(typeof command.ready === 'boolean', '准备状态无效');
        if (command.ready && command.assetKey !== undefined) assert(command.assetKey === roomAssetKey(state), '房间素材已变化，请等待加载后重新准备');
        this.human(state, playerId).ready = command.ready; break;
      case 'map': this.lobby(state); this.human(state, playerId); this.map(String(command.mapId)); state.mapId = String(command.mapId); break;
      case 'addBot': {
        this.lobby(state); this.human(state, playerId); assert(state.players.length < 6, '房间已满');
        const counts = [state.players.filter(p => p.team === 0).length, state.players.filter(p => p.team === 1).length];
        const team: Team = command.team ?? (counts[0] <= counts[1] ? 0 : 1);
        assert((team === 0 || team === 1) && counts[team] < 3, '该队伍已满');
        const weaponId = String(command.weaponId ?? [...this.weapons.keys()][state.players.length % this.weapons.size]);
        const botId = `bot-${Math.floor(random(state) * 0x100000000).toString(16)}`;
        state.players.push(this.newPlayer(botId, command.name ? name(command.name) : `弹弹 ${state.players.filter(p => p.bot).length + 1}`, team, weaponId, true)); break;
      }
      case 'removeBot': this.lobby(state); this.human(state, playerId); assert(this.player(state, command.playerId).bot, '此席位不是机器人'); state.players = state.players.filter(p => p.id !== command.playerId); break;
      case 'start': {
        this.lobby(state); this.human(state, playerId);
        assert(state.players.every(p => p.bot || (p.ready && p.connected)), '等待全部玩家准备');
        const terrain = this.getTerrain({ ...state, craters: [] }), map = this.map(state.mapId);
        state.phase = 'playing'; state.round++; state.craters = []; state.lastShot = null; state.winner = null;
        const groups = [state.players.filter(p => p.team === 0), state.players.filter(p => p.team === 1)];
        const configured = mapSpawns[map.id];
        // Original MapMgr/BaseGame uses team-specific (x,y) points without
        // replacement. Keep y when resolving the source foot probe: projecting
        // from zero sends lower-tier points onto unrelated floating platforms.
        // Dimension binding also keeps real-map fallbacks out of test/custom maps.
        const spawnPoints = configured?.width === terrain.width && configured.height === terrain.height ? configured.teams : undefined;
        const occupied = new Set<string>();
        for (let team = 0; team < 2; team++) groups[team].forEach((p, i) => {
          const w = this.weapon(p.weaponId);
          const point = spawnPoints?.[team]?.[i];
          if (spawnPoints) assert(point && Number.isFinite(point.x) && Number.isFinite(point.y)
            && point.x >= 20 && point.x < terrain.width - 20 && point.y >= 0 && point.y < terrain.height - 1, '地图出生点配置无效');
          const candidates = map.spawnX?.filter(x => Number.isFinite(x) && x >= 20 && x < terrain.width - 20) ?? [];
          const index = team === 0 ? i : candidates.length - 1 - i;
          const preferred = point?.x ?? candidates[index] ?? terrain.width * (team === 0 ? 0.18 + i * 0.11 : 0.82 - i * 0.11);
          let x = Math.round(preferred), y = sourceStandingY(terrain, x, point?.y ?? 0);
          for (let offset = 0; !point && y === null && offset < terrain.width; offset += 15) {
            x = Math.min(terrain.width - 20, Math.max(20, Math.round(preferred + (offset % 30 ? -offset : offset)))); y = sourceStandingY(terrain, x);
          }
          assert(y !== null, '地图上找不到落脚位置');
          if (point) {
            assert(!occupied.has(`${x},${y}`), '地图出生点不能重复');
            occupied.add(`${x},${y}`);
          }
          p.x = x; p.y = y; p.face = team === 0 ? 1 : -1; p.hp = p.maxHp = 1500;
          p.energy = this.energy(w); p.anger = 0; p.alive = true; p.delay = this.baseDelay(w); p.frozenTurns = 0; this.clearProps(p);
          delete p.movementFall;
        });
        state.turn.number = 0; this.nextTurn(state, now); break;
      }
      case 'move': {
        const p = this.turnPlayer(state, playerId, now), dx = finite(command.dx, -32, 32, '移动距离');
        assert(Number.isInteger(dx), '移动距离须为整数');
        const walked = sourceWalk(p, dx, this.getTerrain(state));
        Object.assign(p, walked.player); delete p.movementFall;
        if (!p.alive) this.kill(p);
        if (walked.fall) {
          p.movementFall = { ...walked.fall, startedAt: now };
          state.turn.readyAt = Math.max(state.turn.readyAt, now + walked.fall.duration);
        }
        this.checkWinner(state);
        if (state.phase === 'playing' && !p.alive) this.nextTurn(state, now + (walked.fall?.duration ?? 0));
        break;
      }
      case 'aim': {
        const p = this.turnPlayer(state, playerId, now);
        p.angle = this.aimAngle(p, command.angle);
        if (command.face !== undefined) { assert(command.face === -1 || command.face === 1, '朝向无效'); p.face = command.face; } break;
      }
      case 'useProp': this.activateProp(this.turnPlayer(state, playerId, now), command.propId); break;
      case 'fire': shot = this.fire(state, playerId, command, now); break;
      case 'skip': {
        const p = this.turnPlayer(state, playerId, now); p.delay += 100; p.anger = Math.min(200, p.anger + 40); this.clearProps(p); this.nextTurn(state, now); break;
      }
      case 'rematch':
      case 'endBattle': {
        this.human(state, playerId);
        if (command.type === 'rematch') assert(state.phase === 'finished', '对局尚未结束');
        else assert(state.phase === 'playing' || state.phase === 'finished', '当前没有对局');
        state.phase = 'lobby'; state.winner = null; state.lastShot = null; state.craters = [];
        state.turn = { playerId: '', number: 0, deadline: 0, readyAt: 0, wind: 0 };
        for (const p of state.players) { p.ready = p.bot; p.alive = true; p.frozenTurns = 0; delete p.movementFall; this.clearProps(p); } break;
      }
      default: throw new Error('未知指令');
    }
    // A ready browser loaded the old room's weapon set and both character sexes.
    // A new weapon or map needs another acknowledgement; removing seats only
    // reduces that resource set, so it cannot invalidate an existing ready.
    if (input.phase === 'lobby' && state.phase === 'lobby' && this.requiresNewRoomAssets(input, state)) {
      for (const p of state.players) p.ready = p.bot;
    }
    state.revision++; return { state, ...(shot ? { shot } : {}) };
  }

  tick(input: GameState, now = Date.now()): EngineResult {
    if (input.phase !== 'playing' || now < input.turn.readyAt) return { state: input };
    const state = clone(input), p = state.players.find(player => player.id === state.turn.playerId);
    // Battle seats survive a lost connection for rejoin, but they must not
    // reserve an unplayable turn. Also repair a dead/missing active participant
    // from an older persisted snapshot without passing through its timeout.
    if (!p || !p.alive || (!p.bot && !p.connected) || p.frozenTurns) {
      this.nextTurn(state, now);
      if (state.phase === input.phase && state.turn.number === input.turn.number) return { state: input };
      state.revision++; return { state };
    }
    if (now >= state.turn.deadline) {
      p.delay += 100; p.anger = Math.min(200, p.anger + 40); this.clearProps(p); this.nextTurn(state, now); state.revision++; return { state };
    }
    if (p.bot && now >= state.turn.readyAt + 900) {
      const hasEnemy = state.players.some(target => target.alive && target.team !== p.team);
      const command = hasEnemy ? this.chooseAI(state, p.id) : { type: 'skip' } as const;
      return this.apply(state, p.id, command, now);
    }
    return { state: input };
  }

  private aimAngle(p: PlayerState, value: unknown, fly = (p.queuedProps ?? []).includes('fly')): number {
    const w = this.weapon(p.weaponId);
    // Later-candidate LocalPlayer.gunAngle accepts Bomb3 in 0..90, bypassing
    // armMin/Max; ArrowView.carrayAngle displays the same action sector.
    return finite(value, fly ? 0 : w.minAngle, fly ? 90 : w.maxAngle, '角度');
  }
  private clearProps(p: PlayerState): void {
    p.queuedProps = []; p.queuedPow = false;
    // PrepareNewTurn restores the main ball, and ArrowView resets its sector.
    // Retain the chosen aim where possible without stranding a normal shot at
    // the wider paper-plane angle after firing, skipping or timing out.
    const w = this.weapon(p.weaponId);
    p.angle = Math.max(w.minAngle, Math.min(w.maxAngle, p.angle));
  }
  private validateProps(props: PropId[], special: boolean): void {
    assert(Array.isArray(props) && props.length <= 3, '每次最多选择三个道具');
    assert(new Set(props).size === props.length && props.every(id => Object.hasOwn(PROP_DEFINITIONS, id)), '道具无效');
    assert(!(special && props.includes('trident')), '必杀不能与三叉戟同时使用');
    const fly = props.includes('fly'), ice = props.includes('ice');
    assert(!(fly && ice) && (!(fly || ice) || (props.length === 1 && !special)), '纸飞机或冰冻弹须单独使用');
  }
  private activateProp(p: PlayerState, propId: PropId | 'pow'): void {
    const queued = p.queuedProps ?? [];
    if (propId === 'pow') {
      assert(!p.queuedPow, '本回合已经使用必杀');
      assert(p.anger >= 200, '怒气不足');
      this.validateProps(queued, true);
      // Archived Player.UseSpecialSkill chooses the ball and consumes dander
      // immediately. Activating another command cannot cancel that choice.
      p.anger = 0; p.queuedPow = true; return;
    }
    assert(Object.hasOwn(PROP_DEFINITIONS, propId), '道具无效');
    assert(!queued.includes(propId), '本回合已经使用这个道具');
    this.validateProps([...queued, propId], p.queuedPow === true);
    const definition = PROP_DEFINITIONS[propId];
    assert(p.energy >= definition.energy, '体力不足');
    // Archived Player.UseItem pays energy/delay and broadcasts use before fire.
    p.energy -= definition.energy; p.delay += definition.delay;
    p.queuedProps = [...queued, propId];
    if (propId === 'fly') p.angle = Math.max(0, Math.min(90, p.angle));
  }
  private kill(p: PlayerState): void { p.hp = 0; p.alive = false; p.frozenTurns = 0; this.clearProps(p); }
  private settle(state: GameState, terrain: Terrain): Map<string, Omit<MovementFall, 'startedAt'>> {
    const falls = new Map<string, Omit<MovementFall, 'startedAt'>>();
    for (const p of state.players.filter(p => p.alive)) {
      const from = { x: p.x, y: p.y }, hp = p.hp;
      const y = sourceStandingY(terrain, p.x, p.y);
      p.y = y ?? terrain.height;
      if (y === null) this.kill(p);
      const duration = sourceFallDuration(from.y, p.y);
      if (duration) falls.set(p.id, { from, to: { x: p.x, y: p.y }, duration, ...(y === null ? { hpBeforeDeath: hp } : {}) });
    }
    return falls;
  }
  private checkWinner(state: GameState): void {
    // A room that started with one side is practice: keep taking turns until
    // everyone falls, or a human returns the room to the lobby.
    const contested = state.players.some(p => p.team === 0) && state.players.some(p => p.team === 1);
    if (!contested && state.players.some(p => p.alive)) return;
    const teams = new Set(state.players.filter(p => p.alive).map(p => p.team));
    if (teams.size < 2) {
      state.phase = 'finished'; state.winner = teams.size === 1 ? [...teams][0] : 'draw'; state.turn.deadline = 0;
    }
  }
  private nextTurn(state: GameState, readyAt: number): void {
    this.checkWinner(state); if (state.phase !== 'playing') return;
    // Minimum accumulated delay determines next turn. Frozen turns are consumed
    // on that player's own turn, never on another player's action.
    for (let i = 0; i < 18; i++) {
      const alive = state.players.filter(p => p.alive);
      const available = alive.filter(p => p.bot || p.connected);
      // The Worker pauses an empty battle until a human rejoins. Keep reserved
      // seats and their combat state; never fabricate an empty active player.
      if (!available.length) return;
      const lowest = Math.min(...available.map(p => p.delay));
      const ties = available.filter(p => p.delay === lowest);
      const p = ties[Math.floor(random(state) * ties.length)];
      // Offline participants can become due, but cannot accumulate negative
      // delay and receive many catch-up turns when they reconnect.
      for (const player of alive) player.delay = Math.max(0, player.delay - lowest);
      p.delay += this.baseDelay(this.weapon(p.weaponId));
      state.turn.number++;
      this.clearProps(p);
      if (p.frozenTurns > 0) { p.frozenTurns--; continue; }
      p.energy = this.energy(this.weapon(p.weaponId));
      state.turn = { playerId: p.id, number: state.turn.number, readyAt, deadline: readyAt + TURN_MS, wind: Math.round((random(state) * 10 - 5) * 10) / 10 };
      return;
    }
    throw new Error('无法选择下一回合');
  }

  private launchMotion(p: PlayerState, angle: number, power: number, terrain: Terrain): FlightMotion {
    const { origin, worldAngle } = sourceShotGeometry(terrain, p, angle);
    return this.packetMotion(origin, worldAngle, power);
  }
  private packetMotion(origin: Point, worldAngle: number, power: number): FlightMotion {
    const radians = worldAngle * Math.PI / 180;
    // Archived ShootImp transports integer speed components to the client.
    return { ...origin, vx: Math.trunc(Math.cos(radians) * power * 20), vy: Math.trunc(Math.sin(radians) * power * 20) };
  }
  private stepMotion(state: GameState, motion: FlightMotion, ball: BallDefinition, terrain: Terrain, dt: number): { impact: Point | null; escaped: boolean } {
    const map = this.map(state.mapId);
    const mass = Math.max(1, ball.mass), drag = map.drag * ball.drag;
    const fx = state.turn.wind * ball.wind, fy = map.gravity * ball.weight * mass;
    // Semi-implicit Euler exactly matches archived EulerVector.cs ordering.
    motion.vx += (fx - drag * motion.vx) / mass * dt; motion.vy += (fy - drag * motion.vy) / mass * dt;
    const previous = { x: motion.x, y: motion.y }, next = { x: motion.x + motion.vx * dt, y: motion.y + motion.vy * dt };
    for (const at of sourceBombSweep(previous, next)) {
      // Map.FindPhysicalObjects excludes the bomb itself, not its owner, and
      // Living.CollidedByObject also detonates fly/ice projectiles. Preserve
      // source order: player bounds, tile corners, then map escape.
      if (state.players.some(target => target.alive && sourcePlayerIntersectsBomb(target, at)) || !sourceBombGroundEmpty(terrain, at)) {
        motion.x = at.x; motion.y = at.y; return { impact: at, escaped: false };
      }
      if (at.x < 0 || at.x >= terrain.width || at.y >= terrain.height) {
        motion.x = at.x; motion.y = at.y; return { impact: null, escaped: true };
      }
    }
    motion.x = next.x; motion.y = next.y;
    return { impact: null, escaped: false };
  }
  private simulate(state: GameState, p: PlayerState, angle: number, power: number, ball: BallDefinition, terrain: Terrain, coarse = false): Flight {
    const motion = this.launchMotion(p, angle, power, terrain), points = [{ x: motion.x, y: motion.y, t: 0 }], dt = coarse ? 0.08 : PHYSICS_STEP;
    for (let step = 1; step <= Math.ceil(12 / dt); step++) {
      const { impact, escaped } = this.stepMotion(state, motion, ball, terrain, dt);
      const t = Math.round(step * dt * 1000);
      if (impact) { points.push({ ...impact, t }); return { points, impact, duration: t }; }
      if (step % (coarse ? 1 : 2) === 0) points.push({ x: Math.round(motion.x * 10) / 10, y: Math.round(motion.y * 10) / 10, t });
      if (escaped) {
        points.push({ x: motion.x, y: motion.y, t }); return { points, impact: null, duration: t };
      }
    }
    return { points, impact: null, duration: 12_000 };
  }

  private makeDamage(state: GameState, owner: PlayerState, target: PlayerState, point: Point, ball: BallDefinition, multiplier: number, at: number): DamageEvent | null {
    if (sourcePlayerBlastDistance(target, point) >= ball.radius) return null;
    const distance = sourcePlayerDamageDistance(target, point); if (distance >= ball.radius) return null;
    const w = this.weapon(owner.weaponId), tw = this.weapon(target.weaponId);
    // Archived SimpleBomb.cs: two multiplicative reductions and luck-based crit.
    // Fixed grade=20 and base armor=80 are prototype choices: no equipment system.
    const dr1 = 0.95 * (80 - 3 * 20) / (500 + 80 - 3 * 20);
    const remainingDefence = Math.max(0, tw.defence - w.luck);
    const dr2 = 0.95 * remainingDefence / (600 + remainingDefence);
    const rawDamage = w.baseDamage * (1 + w.attack * 0.001) * (1 - dr1) * (1 - dr2) * ball.power * multiplier * (1 - distance / ball.radius / 4);
    // Both pinned SimpleBomb candidates floor MakeDamage first, then floor a
    // separate critical bonus from that integer. Keep the prototype minimum.
    let angerDamage = Math.max(1, Math.floor(rawDamage));
    const criticalRoll = random(state) * 100 < w.luck * 75 / (800 + w.luck);
    const criticalBonus = criticalRoll ? Math.floor(angerDamage * (0.5 + w.luck * 0.0003)) : 0;
    let critical = criticalBonus > 0, amount = angerDamage + criticalBonus;
    // The Player override applies friendly-fire protection before calling the
    // frost-aware base method, even when that method will absorb the HP loss.
    if (owner.team === target.team && amount >= target.hp) {
      amount = Math.max(0, target.hp - 1); angerDamage = amount; critical = false;
    }
    const gainAnger = () => { target.anger = Math.min(200, target.anger + Math.floor((Math.floor(angerDamage * 2 / 5) + 5) / 2)); };
    // Archived Living.TakeDamage checks frost after damage/crit calculation:
    // the first blast thaws the target without reducing HP. The caller still
    // digs the terrain and settles everyone, so a thawing hit can cause a fall.
    // DamageEvent.frozen means this hit APPLIED freeze; false here means thawed.
    if (target.frozenTurns > 0) {
      target.frozenTurns = 0; gainAnger();
      return { playerId: target.id, amount: 0, critical: false, killed: false, frozen: false, thawed: true, at };
    }
    // Archived player friendly-fire never kills; excavation still can.
    target.hp = Math.max(0, target.hp - amount);
    if (target.hp <= 0) this.kill(target); else gainAnger();
    return { playerId: target.id, amount, critical, killed: !target.alive, frozen: false, at };
  }

  private fire(state: GameState, playerId: string, command: Extract<ClientCommand, { type: 'fire' }>, now: number): ShotResult {
    const p = this.turnPlayer(state, playerId, now), w = this.weapon(p.weaponId);
    const power = finite(command.power, 0, 100, '力度');
    if (command.face !== undefined) { assert(command.face === -1 || command.face === 1, '朝向无效'); p.face = command.face; }
    assert(command.pow === undefined || typeof command.pow === 'boolean', '必杀状态无效');
    const legacyProps = command.props ?? [];
    assert(Array.isArray(legacyProps) && legacyProps.length <= 3, '每次最多选择三个道具');
    assert(new Set(legacyProps).size === legacyProps.length && legacyProps.every(id => Object.hasOwn(PROP_DEFINITIONS, id)), '道具无效');
    const props = [...new Set([...(p.queuedProps ?? []), ...legacyProps])];
    const special = p.queuedPow === true || command.pow === true;
    this.validateProps(props, special);
    const fly = props.includes('fly'), ice = props.includes('ice');
    const angle = this.aimAngle(p, command.angle, fly);
    // Legacy AI/older clients can still commit props with fire. Already-used
    // props are retained without paying twice, even if the payload repeats them.
    for (const propId of legacyProps) if (!(p.queuedProps ?? []).includes(propId)) this.activateProp(p, propId);
    if (special && !p.queuedPow) this.activateProp(p, 'pow');
    const terrain = this.getTerrain(state);
    // Prop projectiles have their own original resources: old Bomb3 for fly
    // (Player.UseFlySkill SetBall(3)) and Bomb1 for the frozen projectile.
    // They must never inherit the currently equipped weapon's ammunition.
    const ball = fly ? this.actionBall('fly') : ice ? this.actionBall('ice') : special ? w.special : w.normal;
    const craterId = fly || ice ? '' : String(ball.craterId ?? '');
    if (craterId) assert(this.craterMasks.has(craterId), `缺少弹坑碰撞数据：${craterId}`);
    p.angle = angle;
    p.delay += Math.floor(Math.max(0, now - (state.turn.deadline - TURN_MS)) / 1000) * 20;
    // UseSpecialSkill already consumed the old dander. Archived Player.Shoot
    // adds the ordinary firing gain after special shots as well.
    p.anger = Math.min(200, p.anger + 20);
    const kind: ShotResult['kind'] = fly ? 'fly' : ice ? 'ice' : special ? 'special' : 'normal';
    const timing = attackTiming(p.weaponId, kind);
    const releaseGeometry = sourceShotGeometry(terrain, p, angle);
    const transition = (player: PlayerState, at: number): PlayerTransition => ({ playerId: player.id, at, x: player.x, y: player.y, hp: player.hp, alive: player.alive, frozenTurns: player.frozenTurns, anger: player.anger, energy: player.energy, face: player.face });
    const shot: ShotResult = { id: `${state.round}:${state.revision + 1}`, playerId, weaponId: p.weaponId, special, kind, angle, power, bodyAngle: releaseGeometry.bodyAngle, createdAt: now, duration: 0, trajectories: [], impacts: [], damage: [], craters: [], playerTransitions: [transition(p, 0)] };
    const bursts = 1 + (props.includes('extra1') ? 1 : 0) + (props.includes('extra2') ? 2 : 0);
    const trident = props.includes('trident');
    const amount = fly || ice ? 1 : trident ? 3 : Math.max(1, Math.min(3, Math.floor(ball.amount || 1)));
    // Prop stacking factors are explicit prototype calibration choices. Later
    // packet/model schemas establish the features, not exact 2009 coefficients.
    const boost = 1 + props.filter(id => id.startsWith('damage')).reduce((sum, id) => sum + Number(id.slice(6)) / 100, 0);
    const multiplier = boost * (bursts > 1 ? 0.7 : 1) * (trident ? 0.6 : 1);
    const fallEnds = new Map<string, number>();
    const applyImpact = (impact: Point, at: number, flightMs: number, velocity: Point) => {
      shot.impacts.push({ ...impact, craterId, at });
      const beforeImpact = state.players.map(player => transition(player, at));
      let flyTranslated = false;
      let falls = new Map<string, Omit<MovementFall, 'startedAt'>>();
      if (fly) {
        // SimpleBomb TRANSLATE first changes the owner's position, then
        // START_MOVE carries Player.StartMoving's final downward support search.
        // Preserve both snapshots in that order at the same collision tick.
        if (impact.y > 10 && flightMs > 40) {
          const translated = sourceFlyTranslatePoint(terrain, impact, velocity);
          p.x = translated.x; p.y = translated.y;
          shot.playerTransitions!.push(transition(p, at)); flyTranslated = true;
          const from = { x: p.x, y: p.y }, hp = p.hp;
          const x = Math.max(0, Math.min(terrain.width - 1, p.x));
          const y = sourceStandingY(terrain, x, p.y);
          p.x = x; p.y = y ?? terrain.height;
          if (y === null) {
            this.kill(p);
            shot.damage.push({ playerId: p.id, amount: 0, critical: false, killed: true, frozen: false, at });
          }
          const duration = sourceFallDuration(from.y, p.y);
          if (duration) falls.set(p.id, { from, to: { x: p.x, y: p.y }, duration, ...(y === null ? { hpBeforeDeath: hp } : {}) });
        }
      } else if (ice) {
        for (const target of state.players.filter(x => x.alive && sourcePlayerBlastDistance(x, impact) < ball.radius)) {
          target.frozenTurns = Math.max(1, target.frozenTurns); shot.damage.push({ playerId: target.id, amount: 0, critical: false, killed: false, frozen: true, at });
        }
      } else {
        for (const target of state.players.filter(x => x.alive)) {
          const damage = this.makeDamage(state, p, target, impact, ball, multiplier, at); if (damage) shot.damage.push(damage);
        }
        if (craterId) {
          const operation: CraterOperation = { ...impact, id: craterId };
          terrain.dig(impact.x, impact.y, this.craterMasks.get(craterId)!); state.craters.push(operation); shot.craters.push(operation);
        }
        const wasAlive = new Set(state.players.filter(x => x.alive).map(x => x.id));
        falls = this.settle(state, terrain);
        for (const target of state.players.filter(x => !x.alive && wasAlive.has(x.id))) shot.damage.push({ playerId: target.id, amount: 0, critical: false, killed: true, frozen: false, at });
      }
      for (const player of state.players) {
        const after = transition(player, at), before = flyTranslated && player.id === p.id
          ? shot.playerTransitions!.at(-1)! : beforeImpact.find(entry => entry.playerId === player.id)!;
        const fall = falls.get(player.id);
        if (fall) {
          // A queued second PlayerFallingAction is prepared on the next25fps
          // tick after the completed action is shifted, then executes later.
          const previousEnd = fallEnds.get(player.id);
          const startedAt = Math.max(at, previousEnd === undefined ? at : previousEnd + 40);
          after.fall = { ...fall, startedAt }; fallEnds.set(player.id, startedAt + fall.duration);
        }
        if (Object.keys(after).some(key => after[key as keyof PlayerTransition] !== before[key as keyof PlayerTransition])) shot.playerTransitions!.push(after);
      }
    };
    const tickMs = PHYSICS_STEP * 1000;
    const active: { motion: FlightMotion; projectile: ProjectileResult; nextAt: number; steps: number }[] = [];
    const requestedAngles: number[] = [];
    let burst = 0;
    while (active.length || (burst < bursts && p.alive)) {
      // All projectiles advance together. An earlier projectile must not dig
      // future terrain or move a character before the next volley is emitted.
      // Retain the source's one-second angle requests. Unlike the archived
      // cached shootPoint, each actual emission follows the visible character:
      // an earlier explosion can move the owner before a later volley launches.
      const requestTime = requestedAngles.length < bursts && p.alive ? requestedAngles.length * 1000 : Infinity;
      const launchTime = burst < requestedAngles.length && p.alive ? timing.windupMs + burst * timing.totalMs : Infinity;
      const at = Math.min(requestTime, launchTime, ...active.map(entry => entry.nextAt));
      for (const entry of active.filter(entry => entry.nextAt === at)) {
        const { impact, escaped } = this.stepMotion(state, entry.motion, ball, terrain, PHYSICS_STEP);
        entry.steps++;
        const { motion, projectile } = entry;
        if (impact) {
          projectile.points.push({ ...impact, t: at }); projectile.impact = impact;
          active.splice(active.indexOf(entry), 1); applyImpact(impact, at, at - projectile.launchDelay, { x: motion.vx, y: motion.vy });
        } else {
          if (entry.steps % 2 === 0) projectile.points.push({ x: Math.round(motion.x * 10) / 10, y: Math.round(motion.y * 10) / 10, t: at });
          if (escaped || entry.steps >= 12 / PHYSICS_STEP) {
            if (projectile.points.at(-1)!.t !== at) projectile.points.push({ x: motion.x, y: motion.y, t: at });
            active.splice(active.indexOf(entry), 1);
          } else entry.nextAt += tickMs;
        }
      }
      if (at === requestTime && p.alive) requestedAngles.push(requestedAngles.length === 0 ? releaseGeometry.worldAngle : sourceShotGeometry(terrain, p, angle).worldAngle);
      // The first request at 0 schedules its emission for a later loop event.
      if (at === launchTime && p.alive) {
        const launchPose = { ...p };
        const falls = shot.playerTransitions!.filter(entry => entry.playerId === p.id && entry.fall);
        const visibleFall = falls.filter(entry => entry.fall!.startedAt <= at).at(-1)?.fall;
        const pendingFall = falls.filter(entry => entry.fall!.startedAt > at).at(-1)?.fall;
        if (visibleFall) {
          launchPose.x = visibleFall.to.x;
          launchPose.y = sourceFallY(visibleFall.from.y, visibleFall.to.y, at - visibleFall.startedAt);
        } else if (pendingFall) {
          launchPose.x = pendingFall.from.x;
          launchPose.y = pendingFall.from.y;
        }
        const launchOrigin = sourceShotGeometry(terrain, launchPose, angle).origin;
        // Every __shoot packet queues a full SHOW -> SHOT/THROWS -> HIDE
        // sequence; its 24-tick hold does not wait for ammunition to land.
        for (let n = 0; n < amount; n++) {
          // Later-candidate II Living.cs:853-883 and 3.0 Living.cs:581-605:
          // centre, global -5/force 0.9, global +5/force 1.1, simultaneous.
          const worldAngleOffset = n === 1 ? -5 : n === 2 ? 5 : 0;
          const forceFactor = n === 1 ? 0.9 : n === 2 ? 1.1 : 1;
          const motion = this.packetMotion(launchOrigin, requestedAngles[burst] + worldAngleOffset, power * forceFactor);
          const projectile: ProjectileResult = { index: shot.trajectories.length, launchDelay: at, points: [{ x: motion.x, y: motion.y, t: at }], impact: null, craterId, projectile: ball.projectile ?? [], explosion: ball.explosion ?? [], spin: ball.spin || 0 };
          shot.trajectories.push(projectile);
          active.push({ motion, projectile, nextAt: at + tickMs, steps: 0 });
        }
        burst++;
      }
    }
    shot.duration = Math.max(
      Math.max(400, ...shot.trajectories.map(x => x.points.at(-1)?.t ?? 0)) + 450,
      ...shot.trajectories.map(x => x.launchDelay + timing.recoveryMs),
      ...[...fallEnds.values()].map(end => end + 40),
    );
    p.delay += 100; this.clearProps(p); state.lastShot = shot; this.checkWinner(state);
    if (state.phase === 'playing') this.nextTurn(state, now + shot.duration);
    return shot;
  }

  /** Bounded aim search: 40 coarse candidates + 15 exact refinements. Results
   * use the same authority calculation as humans. No privileged target hit. */
  chooseAI(state: GameState, playerId: string): Extract<ClientCommand, { type: 'fire' }> {
    const p = this.player(state, playerId), w = this.weapon(p.weaponId), terrain = this.getTerrain(state);
    const enemies = state.players.filter(target => target.alive && target.team !== p.team);
    const target = enemies.reduce((best, current) => !best || Math.abs(current.x - p.x) < Math.abs(best.x - p.x) ? current : best, undefined as PlayerState | undefined);
    assert(target, '没有可攻击的目标');
    // The bot changes only its own facing, as a normal aim command would.
    const aimingPlayer = { ...p, face: (target.x >= p.x ? 1 : -1) as -1 | 1 };
    const ball = p.anger >= 200 ? w.special : w.normal;
    let best = { angle: Math.round((w.minAngle + w.maxAngle) / 2), power: 55, score: Infinity };
    const score = (angle: number, power: number, coarse: boolean): number => {
      const flight = this.simulate(state, aimingPlayer, angle, power, ball, terrain, coarse);
      const endpoint = flight.impact ?? flight.points[flight.points.length - 1];
      const distance = sourcePlayerDamageDistance(target!, endpoint);
      const friendly = state.players.filter(x => x.alive && x.team === p.team).reduce((sum, x) => sum + Math.max(0, ball.radius - sourcePlayerBlastDistance(x, endpoint)) * 2, 0);
      return distance + friendly + (flight.impact ? 0 : 80);
    };
    for (let a = 0; a < 5; a++) for (let f = 0; f < 8; f++) {
      const angle = w.minAngle + (w.maxAngle - w.minAngle) * a / 4, power = 20 + f * 10;
      const value = score(angle, power, true); if (value < best.score) best = { angle, power, score: value };
    }
    const coarseBest = { ...best }; best.score = Infinity;
    for (const da of [-3, 0, 3]) for (const df of [-4, -2, 0, 2, 4]) {
      const angle = Math.max(w.minAngle, Math.min(w.maxAngle, coarseBest.angle + da));
      const power = Math.max(5, Math.min(100, coarseBest.power + df));
      const value = score(angle, power, false); if (value < best.score) best = { angle, power, score: value };
    }
    return { type: 'fire', angle: best.angle, power: best.power, face: aimingPlayer.face, pow: p.anger >= 200, props: p.energy >= 85 && best.score < ball.radius ? ['damage50'] : [] };
  }
}
